import productionPlatformStats from '../data/productionPlatformStats.json';

import { fetchWithRetry, responseError } from './apiError';

export const PRODUCTION_PLATFORM_STATS_URL = 'https://api.8080.ai/api/v1/public/platform-stats';

export interface PlatformStats {
  total_tasks_done: number | null;
  total_pages_generated: number | null;
  estimated_screens_generated: number | null;
  screen_estimate_multiplier: number;
  countries_served: number | null;
  tokens_served: number | null;
  estimated_tokens_processed: number | null;
  token_estimate_multiplier: number;
  updated_at: string | null;
  token_source: 'litellm_daily_usage' | null;
  token_coverage_start: string | null;
}

// Preserve field absence during a rolling deployment without confusing it with recorded nulls or zeroes.
const payloadsWithoutScreens = new WeakSet<PlatformStats>();

const count = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
);
const timestamp = (value: unknown): string | null => (
  typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null
);

export function parsePlatformStats(value: unknown): PlatformStats | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;

  if (!('total_tasks_done' in data) || !('tokens_served' in data) || !('countries_served' in data)) return null;

  const stats: PlatformStats = {
    total_tasks_done: count(data.total_tasks_done), countries_served: count(data.countries_served),
    total_pages_generated: count(data.total_pages_generated),
    estimated_screens_generated: count(data.estimated_screens_generated),
    screen_estimate_multiplier: count(data.screen_estimate_multiplier) ?? 10,
    tokens_served: count(data.tokens_served), estimated_tokens_processed: count(data.estimated_tokens_processed),
    token_estimate_multiplier: count(data.token_estimate_multiplier) ?? 50,
    updated_at: timestamp(data.updated_at), token_coverage_start: timestamp(data.token_coverage_start),
    token_source: data.token_source === 'litellm_daily_usage' ? data.token_source : null,
  };

  if (!('total_pages_generated' in data) && !('estimated_screens_generated' in data)) payloadsWithoutScreens.add(stats);

  return stats;
}

export function verifiedProductionSnapshot(value: unknown): PlatformStats | null {
  if (!value || typeof value !== 'object') return null;
  const snapshot = value as Record<string, unknown>;

  if (snapshot.source !== 'production' || snapshot.source_url !== PRODUCTION_PLATFORM_STATS_URL) return null;
  const stats = parsePlatformStats(snapshot.stats);

  return stats?.updated_at ? stats : null;
}

export function preloadedPlatformStats(): PlatformStats | null {
  const bundled = verifiedProductionSnapshot(productionPlatformStats);
  const preload = typeof window === 'undefined' ? {} : window as {
    __PRELOADED_PLATFORM_STATS__?: unknown; __PRELOADED_PLATFORM_STATS_SOURCE__?: string;
  };
  const injected = preload.__PRELOADED_PLATFORM_STATS_SOURCE__ === 'production'
    ? parsePlatformStats(preload.__PRELOADED_PLATFORM_STATS__) : null;

  return injected?.updated_at ? newestPlatformStats(bundled, injected) : bundled;
}

export function newestPlatformStats(previous: PlatformStats | null, incoming: PlatformStats): PlatformStats {
  if (previous && !incoming.updated_at
    && [incoming.total_tasks_done, incoming.estimated_screens_generated,
      incoming.tokens_served, incoming.countries_served]
      .every((value) => value === null)) {
    return previous;
  }
  if (previous?.updated_at && incoming.updated_at
    && Date.parse(previous.updated_at) > Date.parse(incoming.updated_at)) {
    return previous;
  }

  return retainScreensDuringRollout(previous, incoming);
}

function retainScreensDuringRollout(previous: PlatformStats | null, incoming: PlatformStats): PlatformStats {
  if (!previous || !payloadsWithoutScreens.has(incoming)
    || (previous.total_pages_generated === null && previous.estimated_screens_generated === null)) return incoming;

  return { ...incoming, total_pages_generated: previous.total_pages_generated,
    estimated_screens_generated: previous.estimated_screens_generated,
    screen_estimate_multiplier: previous.screen_estimate_multiplier };
}

export async function getPlatformStats(signal: globalThis.AbortSignal): Promise<PlatformStats> {
  const response = await fetchWithRetry(PRODUCTION_PLATFORM_STATS_URL, {
    signal, cache: 'no-store', credentials: 'omit',
  });

  if (!response.ok) throw await responseError(response);
  const data = parsePlatformStats(await response.json());

  if (!data) throw new Error('Platform statistics are unavailable.');

  return data;
}

export const formatPlatformCount = (value: number): string => value.toLocaleString('en-US', {
  notation: value >= 1000000 ? 'compact' : 'standard', maximumFractionDigits: value >= 1000000 ? 1 : 0,
});

export const platformStatsUtcTime = (value: string): string => `${new Date(value).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
